import { useEffect, useRef, useState } from 'react'
import { Check, Search } from 'lucide-react'

/**
 * `BuilderPageAutocomplete` — recherche à autocomplétion sur les pages d'un
 * site : un champ de recherche débouncé au-dessus d'une liste de boutons
 * défilante, pas un `role="combobox"` flottant.
 *
 * La recherche se fait CÔTÉ SERVEUR (`/api/builder/pages?site=…&q=…&limit=…`).
 * Un site peut porter plus de 200 pages ; charger la liste entière pour n'en
 * choisir qu'une — ce que faisaient le `<select>` de « Partir de » et le
 * sélecteur de page interne de `LinkEditor` — téléchargeait des centaines de
 * lignes à chaque ouverture, et mélangeait à celles du site les pages de TOUS
 * les autres. `limit` (10 par défaut) borne chaque réponse ; c'est un opt-in
 * porté par ce composant, pas par `useBuilderPages()` (cf. sa doc : les autres
 * appelants comptent sur la liste complète).
 *
 * Props :
 *  - siteId      : cloisonne la recherche (relation de groupe, côté serveur).
 *  - requireSite : sans site, ne rien chercher (défaut) — c'est le cas du
 *                  sélecteur « Partir de », dont la question n'a pas de sens
 *                  hors d'un site. `false` cherche alors parmi toutes les pages
 *                  visibles : une page sans site n'a aucun contexte auquel se
 *                  limiter, et son lien interne doit rester choisissable.
 *  - value       : slug sélectionné, "" pour l'option « page vierge ».
 *  - onChange    : (slug, page|null) — `page` est `null` pour l'option vierge.
 *  - blankLabel  : intitulé de l'option vierge, toujours épinglée en tête ;
 *                  `null` la retire (« partir de rien » n'est pas une adresse).
 *  - limit       : nombre de résultats par requête (défaut 10).
 *  - id, describedById : pour l'association du `<label>` externe et de l'aide.
 */
export const BuilderPageAutocomplete = ({
  siteId = null,
  requireSite = true,
  value = '',
  onChange,
  blankLabel = 'Une page vierge',
  limit = 10,
  id,
  describedById,
}) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(true)
  const debounceRef = useRef(null)
  const requestRef = useRef(0)

  // Recherche débouncée : un aller-retour par frappe suffirait à saturer le
  // serveur sur une saisie rapide. `requestRef` écarte une réponse arrivée en
  // retard (requête précédente encore en vol) qui écraserait un résultat plus
  // récent — l'ordre réseau n'est pas garanti par l'ordre d'émission.
  useEffect(() => {
    if (!siteId && requireSite) { setResults([]); setLoading(false); return undefined }
    setLoading(true)
    const myRequest = ++requestRef.current
    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ limit: String(limit) })
        if (siteId) params.set('site', String(siteId))
        if (query.trim()) params.set('q', query.trim())
        const res = await fetch(`/api/builder/pages?${params}`, { credentials: 'include' })
        if (myRequest !== requestRef.current) return
        setResults(res.ok ? await res.json() : [])
      } catch {
        if (myRequest === requestRef.current) setResults([])
      } finally {
        if (myRequest === requestRef.current) setLoading(false)
      }
    }, 250)
    return () => clearTimeout(debounceRef.current)
  }, [siteId, requireSite, query, limit])

  const selectedPage = results.find(p => p.slug === value) ?? null
  // Le résultat sélectionné peut être HORS de la page courante de recherche
  // (choisi avant une nouvelle frappe) : son titre reste affiché quand même,
  // faute de quoi choisir puis chercher autre chose ferait mine d'avoir perdu
  // la sélection alors qu'elle tient toujours (`value` ne change qu'au clic).
  const selectedLabel = value === ''
    ? (blankLabel ?? 'aucune page')
    : (selectedPage?.title || value)

  return (
    <div>
      <div className="relative">
        <Search size={14} aria-hidden="true" className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          id={id}
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Rechercher une page…"
          aria-describedby={describedById}
          className="input w-full pl-8"
        />
      </div>

      <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
        {/* Épinglée en tête, jamais filtrée par la recherche : ce n'est pas une
            page à trouver, c'est l'option « ne partir de rien ». */}
        {blankLabel && (
          <button
            type="button"
            onClick={() => onChange('', null)}
            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors ${
              value === '' ? 'bg-iris-50 text-iris-800' : 'hover:bg-slate-50 text-slate-700'
            }`}
          >
            <span className="italic">{blankLabel}</span>
            {value === '' && <Check size={14} className="shrink-0 text-iris-600" aria-hidden="true" />}
          </button>
        )}

        {loading && <p className="px-3 py-2 text-xs text-slate-400">Recherche…</p>}
        {!loading && results.length === 0 && (
          <p className="px-3 py-2 text-xs text-slate-400">
            {query.trim()
              ? 'Aucune page ne correspond.'
              : (siteId ? 'Ce site n’a pas encore de page.' : 'Aucune page à proposer.')}
          </p>
        )}
        {!loading && results.map(page => (
          <button
            key={page.slug}
            type="button"
            onClick={() => onChange(page.slug, page)}
            className={`flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors ${
              value === page.slug ? 'bg-iris-50 text-iris-800' : 'hover:bg-slate-50 text-slate-700'
            }`}
          >
            {/* Le slug SOUS le titre : sur un site repris, plusieurs pages
                portent le même titre — sans lui, la liste est indécidable. */}
            <span className="min-w-0">
              <span className="block truncate">{page.title || page.slug}</span>
              <span className="block truncate font-mono text-2xs text-slate-400">/{page.slug}</span>
            </span>
            {value === page.slug && <Check size={14} className="shrink-0 text-iris-600" aria-hidden="true" />}
          </button>
        ))}
      </div>

      <p className="mt-1.5 text-xs text-slate-500">
        Sélection actuelle : <strong>{selectedLabel}</strong>
        {!loading && results.length >= limit && (
          <span> — affinez la recherche pour voir d&apos;autres pages.</span>
        )}
      </p>
    </div>
  )
}

export default BuilderPageAutocomplete
